import {Action, Dispatch} from 'redux';
import isFunction = require('lodash/isFunction');
import {isAudioVideoUnsupported, unsupportedMessage} from '../../../utils/helpers';
import {IVideoSettings, VideoSettingsLS} from './local-storage';
import {videoConstraints} from './video-constraints';
import {ERRORS, NOT_SUPPORTED_ERROR} from './errors';

import {
  LOAD_MEDIA_SUCCESS,
  LOAD_MEDIA_START,
  LOAD_MEDIA_FAIL,

  SAVE_VIDEO_SETTINGS,

  SET_AUDIO_OUTPUT_SUCCESS,
  SET_AUDIO_OUTPUT_START,
  SET_AUDIO_OUTPUT_FAIL,

  SET_MEDIA_INPUT_SUCCESS,
  SET_MEDIA_INPUT_START,
  SET_MEDIA_INPUT_FAIL,

  SET_VIDEO_QUALITY_SUCCESS,
  SET_VIDEO_QUALITY_START,
  SET_VIDEO_QUALITY_FAIL,

  AUDIO_OUTPUT,
  AUDIO_INPUT,
  VIDEO_INPUT,

  ERROR_SCREEN,
  ERROR,
} from './constants';

interface IDevice {
  label: string;
  value: string;
}

export interface IVideoSettingsAction extends Action {
  payload?: any;
}

export interface IHTMLAudioElement extends HTMLAudioElement {
  setSinkId: (deviceId: string) => Promise<any>;
}

export interface IMediaInput {
  audioInputDeviceId: string;
  videoInputDeviceId: string;
  videoQualityId: string;
  type?: string;
}

export interface IAudioOutput {
  audioOutputDeviceId: string;
  element: IHTMLAudioElement;
}

export interface ILoadMediaInput {
  audioInputDeviceId: string;
  videoInputDeviceId: string;
  videoQualityId: string;
}

const isWebRTCSupported = navigator.mediaDevices && isFunction(navigator.mediaDevices.getUserMedia);
const normalizeDevice = ({deviceId: value, label}): IDevice => ({value, label});

const getMediaDevices = async () => {
  const devices = await navigator.mediaDevices.enumerateDevices();
  const filterBy = (type: string) => (d) => (
    d.kind === type.toLowerCase() && d.label && d.label.length
  );

  const audioOutputDevices = devices
    .filter(filterBy(AUDIO_OUTPUT))
    .map(normalizeDevice);

  const videoInputDevices = devices
    .filter(filterBy(VIDEO_INPUT))
    .map(normalizeDevice);

  const audioInputDevices = devices
    .filter(filterBy(AUDIO_INPUT))
    .map(normalizeDevice);

  return {
    audioOutputDevices,
    videoInputDevices,
    audioInputDevices,
  };
};

const getMediaStream = ({
  audioInputDeviceId,
  videoInputDeviceId,
  videoQualityId,
}: IMediaInput) => {
  if (isWebRTCSupported) {
    return navigator
      .mediaDevices
      .getUserMedia({
        video: videoConstraints(videoInputDeviceId, videoQualityId, true),
        audio: {deviceId: audioInputDeviceId || undefined},
      } as MediaStreamConstraints);
  }

  const error = new Error();
  error.name = NOT_SUPPORTED_ERROR;

  throw error;
};

const formatDeviceErrorMessage = ({name, message}): string => {
  if (ERRORS[name]) {
    return ERRORS[name];
  }

  const prefix: string = 'Your browser is blocking access to your audio/video devices';
  const moreInfo: string = message || name;
  const suffix: string = moreInfo ? `.` : `, here's some more info: ${moreInfo}`;
  return `${prefix}${suffix}`;
};

export const loadMedia = ({
  audioInputDeviceId,
  videoInputDeviceId,
  videoQualityId,
}: ILoadMediaInput) => async (dispatch: Dispatch<Action>) => {
  if (isAudioVideoUnsupported()) {
    dispatch({
      type: LOAD_MEDIA_FAIL,
      payload: {screenType: ERROR_SCREEN, [ERROR]: unsupportedMessage(true)},
    });
    return;
  }

  try {
    dispatch({type: LOAD_MEDIA_START});

    const stream = await getMediaStream({videoInputDeviceId, audioInputDeviceId, videoQualityId});
    const isSetSinkIdSupported = isFunction(document.createElement) && 'setSinkId' in document.createElement('video');
    const {
      audioOutputDevices,
      audioInputDevices,
      videoInputDevices,
    } = await getMediaDevices();

    const payload = {
      isSetSinkIdSupported,
      audioOutputDevices,
      audioInputDevices,
      videoInputDevices,
      [AUDIO_INPUT]: {deviceId: audioInputDeviceId, error: ''},
      [VIDEO_INPUT]: {deviceId: videoInputDeviceId, error: ''},
      stream,
    };

    dispatch({type: LOAD_MEDIA_SUCCESS, payload});
  } catch (e) {
    dispatch({
      type: LOAD_MEDIA_FAIL,
      payload: {screenType: ERROR_SCREEN, [ERROR]: formatDeviceErrorMessage(e)},
    });
  }
};

export const selectMediaInput = ({
  videoInputDeviceId,
  audioInputDeviceId,
  videoQualityId,
  type,
}: IMediaInput) => (dispatch: Dispatch<Action>) => {
  dispatch({type: SET_MEDIA_INPUT_START});

  const deviceId = {
    [AUDIO_INPUT]: audioInputDeviceId,
    [VIDEO_INPUT]: videoInputDeviceId,
  };

  const action = (actionType, stream: MediaStream, error: string = '') => dispatch({
    type: actionType,
    payload: {
      deviceId: deviceId[type],
      stream,
      error,
      type,
    },
  });

  getMediaStream({videoInputDeviceId, audioInputDeviceId, videoQualityId})
    .then(stream => action(SET_MEDIA_INPUT_SUCCESS, stream))
    .catch(({name, message}) =>  action(SET_MEDIA_INPUT_FAIL, null, message || name));
};

export const selectAudioOutput = ({element, audioOutputDeviceId: deviceId}: IAudioOutput) => (dispatch: Dispatch<Action>) => {
  dispatch({type: SET_AUDIO_OUTPUT_START});

  const action = (actionType, error: string = '') => dispatch({
    type: actionType,
    payload: {deviceId, error},
  });

  element
    .setSinkId(deviceId)
    .then(() => action(SET_AUDIO_OUTPUT_SUCCESS))
    .catch(({name, message}) => action(SET_AUDIO_OUTPUT_FAIL, message || name));
};

export const setVideoQuality = ({
  videoInputDeviceId,
  audioInputDeviceId,
  videoQualityId,
}: IMediaInput) => (dispatch: Dispatch<Action>) => {
  dispatch({type: SET_VIDEO_QUALITY_START});

  const action = (actionType, stream: MediaStream, error: string = '') => dispatch({
    type: actionType,
    payload: {stream, error, videoQualityId},
  });

  getMediaStream({videoInputDeviceId, audioInputDeviceId, videoQualityId})
    .then(stream => action(SET_VIDEO_QUALITY_SUCCESS, stream))
    .catch(({name, message}) =>  action(SET_VIDEO_QUALITY_FAIL, null, message || name));
};

export const saveVideoSettings = (settings: IVideoSettings) => (dispatch: Dispatch<Action>) => {
  VideoSettingsLS.save(settings);
  dispatch({type: SAVE_VIDEO_SETTINGS, payload: settings});
};


